import { forwardRef } from 'react';
import NumberBadge from '@/atoms/badges/NumberBadge';
import Shape from '@enums/Shape';
import Size from '@enums/Size';
import LoadingButton from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Analytics from '@pkg/analytics/Analytics';
import excludeProps from '@/lib/theme/excludeProps';
import ButtonColors from './colors/ButtonColors';
import ButtonSize from './utils/ButtonSize';

const StyledButton = styled(LoadingButton, {
  shouldForwardProp: excludeProps('textProps', 'color', 'hasLabel'),
})(({ hasLabel, textProps, color }) => ({
  ...textProps,
  ...color.normal,
  '&: hover': {
    ...color.hover,
  },
  '&: focus-visible': {
    ...color.focus,
  },
  '&: focus': {
    ...color.normal,
  },
  '& .MuiButton-startIcon': {
    marginRight: hasLabel ? '4px' : 0,
  },
  '& .MuiButton-endIcon': {
    marginLeft: hasLabel ? '4px' : 0,
  },
  '&.Mui-disabled': {
    opacity: 0.75,
    backgroundColor: color.normal.backgroundColor,
  },
  '& .MuiLoadingButton-loadingIndicator': {
    color: color.normal.color,
  },
}));

const Button = forwardRef(function Button(
  {
    badgeContent,
    badgeFirst,
    boldText,
    color = 'primary',
    colorProps,
    dataTestid,
    disabled,
    endIcon,
    fullWidth,
    label,
    loading,
    loadingIndicator,
    onClick,
    size = Size.MEDIUM,
    startIcon,
    track,
    variant = 'contained',
    onMouseDown,
    onMouseUp,
    ...props
  },
  ref
) {
  const textProps = { ...ButtonSize[size] };
  const colors = colorProps ?? ButtonColors[color][variant];

  if (boldText) {
    textProps.fontWeight = 500;
  }

  const handleClick = (event) => {
    if (track) {
      const { name, properties } = track;
      Analytics.track(name, properties);
    }

    onClick?.(event);
  };

  return (
    <StyledButton
      color={colors}
      data-testid={dataTestid}
      disableElevation
      disabled={disabled}
      endIcon={endIcon}
      loading={loading}
      loadingIndicator={loadingIndicator}
      fullWidth={fullWidth}
      onClick={handleClick}
      ref={ref}
      startIcon={startIcon}
      textProps={textProps}
      variant={variant}
      hasLabel={label ? true : false}
      {...props}
    >
      {badgeFirst && Boolean(badgeContent) && (
        <NumberBadge
          number={badgeContent}
          shape={Shape.SQUARE}
          size={Size.X_SMALL}
          overrideStyles={{ ml: 0, mr: 0.5 }}
        />
      )}
      {label}
      {!badgeFirst && Boolean(badgeContent) && (
        <NumberBadge
          number={badgeContent}
          shape={Shape.SQUARE}
          size={Size.X_SMALL}
        />
      )}
    </StyledButton>
  );
});

export default Button;
